import { useState, useCallback } from 'react';
import { Box, Flex, Center } from '@chakra-ui/react';
import { colors } from '@/theme/foundations/colors';
import { fontSizes } from '@/theme/foundations/fontSizes';
import { space } from '@/theme/foundations/space';
import { fontWeights } from '@/theme/foundations/fontWeights';
import { useShowToast } from '@/hooks';
import { useScript } from '@/hooks/use-script';
// Import React and ReactDOM for Checkr embeds
import React from 'react';
import ReactDOM from 'react-dom';
import { KDrawer } from '@/ui-library/components';
import { KText } from '@/ui-library/components/text';
import { Icon } from '@chakra-ui/react';
import { Checkr } from '@/assets/icons/ats';
import { checkrCallback } from './checkrApi';

const ADD_TO_CHECKR_EMBED =
  'https://cdn.jsdelivr.net/npm/@checkr/web-sdk/dist/web-sdk.umd.js';

// Minimal Checkr embed styles: only override button and header color
const checkrEmbedStyles = {
  '.btn-primary': {
    'z-index': '10',
    background: 'linear-gradient(180deg, #605AFB 0%, #503BF0 100%)',
    'border-color': 'transparent',
    color: colors['base-white'],
    'border-radius': '6px !important',
    'margin-top': '80px',
  },
  '.form-control': {
    padding: `${space.xs} ${space.sm} !important`,
    'font-size': fontSizes.sm,
    color: colors['secondary-700'],
  },
  '.header': {
    visibility: 'hidden',
    'margin-bottom': '0px',
  },
  '.form-label': {
    'font-size': fontSizes.sm,
    color: colors['secondary-900'],
    'font-weight': fontWeights.medium,
  },
  '.typeahead-option': {
    color: colors['secondary-700'],
    background: colors['base-white'],
    'font-size': fontSizes.sm,
  },
  '.typeahead-option:hover': {
    background: colors['primary-100'],
    color: colors['secondary-700'],
    'font-weight': fontWeights.medium,
  },
  '.typeahead-option-selected': {
    color: colors['secondary-700'],
    background: colors['primary-100'],
    'font-size': fontSizes.sm,
  },
  '.form-control-clear-typeahead': {
    color: colors['secondary-700'],
  },
  '.form-group-typeahead .form-control-container:after': {
    color: colors['secondary-700'],
  },
};

// Accept isOpen and onClose as props
export const AddToCheckr = ({
  modalActions,
  contactDetails,
  refetchApplication,
}) => {
  const [CheckrEmbeds, setCheckrEmbeds] = useState(null);
  const toast = useShowToast();

  // Handle script ready callback
  const handleScriptReady = useCallback(() => {
    if ((window as any).Checkr?.Embeds) {
      setCheckrEmbeds((window as any).Checkr.Embeds);
    }
  }, []);

  // Use the useScript hook for dynamic script loading
  useScript({
    scriptId: 'checkr-add-to-sdk',
    scriptUrl: 'checkr-add-to-sdk',
    src: ADD_TO_CHECKR_EMBED,
    onReady: handleScriptReady,
  });

  // Create NewInvitation component when embeds are available
  const NewInvitation = CheckrEmbeds?.NewInvitation?.useReact?.(
    React,
    ReactDOM
  );

  const rawPhone =
    contactDetails?.contact?.phone_number ||
    contactDetails?.contact?.local_phone_number ||
    '';

  const formatPhoneForCheckr = (phone: string): string => {
    if (!phone) return '';
    let digitsOnly = phone.replace(/\D/g, '');

    // US/Canada: strip leading country code 1
    if (digitsOnly?.startsWith('1') && digitsOnly?.length === 11) {
      digitsOnly = digitsOnly?.slice(1);
      return digitsOnly;
    } else if (digitsOnly?.length === 10) {
      return digitsOnly;
    }
    return '';
  };

  const formattedPhone = formatPhoneForCheckr(rawPhone);

  // Handle invitation success
  const handleInvitationSuccess = async (response) => {
    if (response?.id) {
      const result = await checkrCallback({
        ats_application_id: response?.external_candidate_id,
        invitation_id: response?.invitation?.id,
        metadata: {
          package_name: response?.package?.name,
        },
      });
      // @ts-ignore
      if (result?.data?.errors?.length === 0) {
        toast({
          title: 'The candiate was invited to the background check',
        });
        modalActions?.onClose();
        refetchApplication();
      } else {
        toast({
          title: 'Background check failed to start. Try again later.',
          status: 'error',
        });
        modalActions?.onClose();
      }
    }
  };

  // Handle invitation error
  const handleInvitationError = (error) => {
    console.error('Invitation failed:', error);
  };

  const renderCheckrHeader = () => {
    return (
      <>
        <Flex alignItems="center" gap="4px" px="xxl" pt="xxl" pb="12px">
          <KText size="md-medium" color={colors['secondary-900']} mr="6px">
            Create
          </KText>
          <Icon as={Checkr} w="24px" h="24px" />
          <KText size="md-medium" color={colors['secondary-900']}>
            Checkr background check
          </KText>
        </Flex>
        <Box border="1px solid" borderColor="border-color" />
      </>
    );
  };

  return (
    <KDrawer
      isOpen={modalActions.isOpen}
      onClose={modalActions.onClose}
      header={renderCheckrHeader()}
      showCloseButton
      skipFooter
      contentProps={{
        borderTopLeftRadius: 'xl',
        size: 'md',
        maxW: '40%',
      }}
    >
      <Flex h="100%" flexDir="column" px="24px">
        {NewInvitation ? (
          <Box minH="84vh">
            <NewInvitation
              sessionTokenPath={`/api_ats/v1/checkr/session_token?ats_application_id=${contactDetails?.id}`}
              onInvitationSuccess={handleInvitationSuccess}
              onInvitationError={handleInvitationError}
              enableLogging={true}
              defaultEmail={contactDetails?.contact?.email}
              defaultPhone={formattedPhone}
              sessionTokenRequestHeaders={() => ({
                Authorization: `Bearer ${localStorage.getItem('apiv1Auth')}`,
              })}
              externalCandidateId={contactDetails?.id?.toString()}
              styles={checkrEmbedStyles}
              hideBackButton={true}
            />
          </Box>
        ) : (
          <Center h="100vh" color={colors['secondary-700']}>
            Loading Checkr ...
          </Center>
        )}
      </Flex>
    </KDrawer>
  );
};
